import { logD, logW } from "@blacknut/logging/dist";
import { State } from "core/redux";
import { hasIncubatingFeature, IncubatingFeaturesKey } from "core/utils";
import * as H from "history";
import React, { PropsWithChildren, RefObject, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  NavigateFunction,
  NavigateOptions,
  To,
  useNavigate,
} from "react-router-dom-v5-compat";
const LOGGING_TAG = "Navigation";
const MAX_GAMES_DEPTH = 4;
const LEVEL_1 = ["/catalog", "/plus", "/myList", "/browse", "search"];
const LEVEL_3 = [
  "/account/profile/[a-z0-9]+",
  "/account/profiles/new",
  "/account/speedtest",
  "/account/personalization",
  "/account/id",
  "/account/settings/advanced",
  "/game/[a-z0-9]+/play",
];

const isGamePage = (to: To) => {
  const pathname = typeof to === "string" ? to : to.pathname;
  return pathname?.match(/\/game\/[a-z0-9]+/);
};

const isLevel1 = (to: To) => {
  const pathname = typeof to === "string" ? to : to.pathname;
  return !!LEVEL_1.find((path) => {
    const re = new RegExp(path);
    return pathname?.match(re);
  });
};

const isLevel3 = (to: To) => {
  const pathname = typeof to === "string" ? to : to.pathname;
  return !!LEVEL_3.find((path) => {
    const re = new RegExp(path);
    return pathname?.match(re);
  });
};
const NOPE = () => logD(LOGGING_TAG, "NOPE");
const NavigationContext = React.createContext<{
  goBack: () => void;
  navigate: NavigateFunction;
}>({ navigate: NOPE, goBack: NOPE });

const NavigationContextProvider = (
  props: PropsWithChildren<unknown & { refRouter: RefObject<any> }>,
) => {
  const history = useHistory();
  const { incubatingFeatures } = useSelector((state: State) => state.globalState);
  const _navigate = useNavigate();

  const notifyUpdate = useCallback(() => {
    if (
      props.refRouter.current &&
      "update" in props.refRouter.current &&
      typeof props.refRouter.current.update === "function"
    ) {
      props.refRouter.current.update();
    }
  }, [props.refRouter]);

  useEffect(() => {
    if ("entries" in history) {
      const _history = history as H.MemoryHistory;
      logD(
        LOGGING_TAG,
        "History: %o. Current: %o",
        _history.entries.map((e) => e.pathname),
        _history.entries[_history.index],
      );
    }

    const unlisten = history.listen(() => {
      if ("entries" in history) {
        const _history = history as H.MemoryHistory;
        logD(
          LOGGING_TAG,
          "History: %o. Current: %o",
          _history.entries.map((e) => e.pathname),
          _history.entries[_history.index],
        );
      }
    });
    return () => {
      unlisten();
    };
  }, [history]);

  const useTVNavigation = hasIncubatingFeature(
    incubatingFeatures,
    IncubatingFeaturesKey.INCUBATING_FEATURE_TV_NAVIGATION,
  );
  const navigate: NavigateFunction = useCallback(
    (to, opts?) => {
      if (useTVNavigation) {
        if (!("entries" in history)) {
          logW(LOGGING_TAG, "Not using Memory router");
          return;
        }
        if (typeof to === "number") {
          //not used yet
          _navigate(to);
          return;
        }
        const pathname = to.toString();
        const _history = history as H.MemoryHistory;
        if (
          typeof opts === "object" &&
          opts &&
          "replace" in opts &&
          opts.replace &&
          !isLevel1(to)
        ) {
          // Prune history
          _history.entries = [..._history.entries.slice(0, _history.index)];
        } else if (pathname.match(/\/welcome/)) {
          //level 0 catalog
          logD(LOGGING_TAG, "Reset memory history");
          _history.entries = [];
        } else if (isLevel1(pathname)) {
          logD(LOGGING_TAG, "Navigate to level 1 (%o)", pathname);
          if (pathname === "/catalog") {
            _history.entries = [];
          } else {
            _history.entries = [
              {
                pathname: "/catalog",
                state: undefined,
                search: "",
                hash: "",
                key: "default",
              },
            ];
          }
        } else if (isLevel3(pathname)) {
          // Level 3
          // /account/speedtest
          // /account/personalization
          // /account/id
          // /account/settings/advanced
          logD(LOGGING_TAG, "Navigate to level 3 (%o)", pathname);
          _navigate(to, opts as NavigateOptions);
          return;
        } else {
          logD(LOGGING_TAG, "Navigate to level 2 (%o)", pathname);

          // /account/profiles/switch
          // /account/profiles/manage,
          // /faq, /account/manageAccount,
          // /account/faq,
          // /account/discord,
          // /account/support,
          // /account/settings

          // for games page retain up to MAX_GAMES_DEPTH games page
          if (isGamePage(pathname)) {
            if (_history.length >= 2) {
              const newEntries: H.Location<unknown>[] = [];
              const oldEntries = _history.entries;
              for (let i = 0; i < history.length; i++) {
                const curr = oldEntries[i];
                if (isGamePage(curr)) {
                  newEntries.push(...oldEntries.slice(i).slice(-MAX_GAMES_DEPTH + 1));
                  break;
                } else {
                  newEntries.push(curr);
                }
              }
              _history.entries = newEntries;
            }
          } else {
            // Level 2, all others
            // Retain only catalog
            _history.entries = [
              {
                pathname: "/catalog",
                key: "",
                search: "",
                state: undefined,
                hash: "",
              },
            ];
          }
        }
        _history.index = _history.entries.length - 1;
        _history.length = _history.entries.length;
        _history.push(pathname);
      } else {
        if (typeof to === "number") {
          _navigate(to);
        } else {
          _navigate(to, opts as NavigateOptions);
        }
      }
    },
    [_navigate, history, useTVNavigation],
  );

  const goBack = useCallback(() => {
    if (useTVNavigation) {
      // prun history
      if (!("entries" in history)) {
        logW(LOGGING_TAG, "Not using Memory router");
        return;
      }
      const _history = history as H.MemoryHistory;
      _navigate(-1);
      logD(
        LOGGING_TAG,
        "Prune history",
        JSON.stringify(_history.entries),
        _history.index,
      );
      _history.entries = _history.entries.slice(0, _history.index + 1);
      _history.length = _history.entries.length;
      notifyUpdate();
    } else {
      _navigate(-1);
    }
  }, [_navigate, history, notifyUpdate, useTVNavigation]);

  return (
    <NavigationContext.Provider value={{ goBack, navigate }}>
      {props.children}
    </NavigationContext.Provider>
  );
};
const useAppNavigation = () => {
  return React.useContext(NavigationContext);
};
export { NavigationContextProvider, useAppNavigation };
